import { useState, useRef, useEffect } from 'react';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';

type Props = {
  src: string;
  onClose: () => void;
  onSave: (croppedImage: string) => void;
  title?: string;
  circular?: boolean;
};

export default function AvatarEditorModal({
  src,
  onClose,
  onSave,
  title = 'アバター画像を編集',
  circular = true,
}: Props) {
  const [crop, setCrop] = useState<Crop>();
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [src]);

  useEffect(() => {
    if (!isImageLoaded || !imageRef.current) return;
    const img = imageRef.current;
    if (circular) {
      const size = Math.min(img.width, img.height);
      setCrop({
        unit: 'px',
        width: size,
        height: size,
        x: (img.width - size) / 2,
        y: (img.height - size) / 2,
      });
    } else {
      setCrop({
        unit: 'px',
        width: img.width,
        height: img.height,
        x: 0,
        y: 0,
      });
    }
  }, [isImageLoaded, circular]);

  const generateCroppedImg = (): string | null => {
    if (!imageRef.current || !crop) {
      return null;
    }

    const img = imageRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      if (circular) {
        // NOTE: 円形にクロップ
        ctx.beginPath();
        ctx.arc(crop.width / 2, crop.height / 2, crop.width / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }

      ctx.drawImage(
        img,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );

      const base64Image = canvas.toDataURL('image/png', 1.0);
      return base64Image;
    }
    return null;
  };

  const handleSave = () => {
    const generatedImage = generateCroppedImg();
    if (!generatedImage) {
      return;
    }
    onSave(generatedImage);
    onClose();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="w-[520px] rounded">
        <DialogHeader className="items-start space-y-0 px-lg! py-md!">
          <DialogTitle className="text-base font-bold">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex justify-center px-lg py-md">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            circularCrop={circular}
            keepSelection
            aspect={circular ? 1 : undefined}
          >
            <img
              ref={imageRef}
              onLoad={() => setIsImageLoaded(true)}
              src={src}
              alt="編集する画像"
              className="max-h-[400px] object-contain"
            />
          </ReactCrop>
        </div>

        <DialogFooter className="gap-md px-lg! py-md!">
          <Button color="secondary" size="md" onClick={onClose}>
            キャンセル
          </Button>
          <Button color="primary" size="md" onClick={handleSave}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
